/**
 * Cart Rewards – free shipping + gift progress bar with automatic gift add/remove.
 *
 * Configuration comes from window.cartRewards (snippets/cart-rewards-config.liquid).
 * The visual bar is rendered server-side by snippets/cart-rewards-bar.liquid and
 * kept in sync here via the theme's "cart:change" / "cart:refresh" events.
 *
 * Gift lines are identified by the line item property "__gift" whose value is
 * the tier key (e.g. "gift_1"). Properties starting with an underscore are hidden
 * in the cart, checkout and order details. The actual price reduction is done by
 * a manually created Shopify discount – this script only adds/removes gift lines.
 */
(() => {
  const config = window.cartRewards;

  if (!config || !config.enabled) {
    return;
  }

  const PROPERTY = config.property || '__gift';

  let debugEnabled = false;
  try {
    debugEnabled = !!config.debug || localStorage.getItem('cartRewardsDebug') === '1';
  } catch (error) {
    debugEnabled = !!config.debug;
  }
  const log = (...args) => debugEnabled && console.log('%c[cart-rewards]', 'color:#2d5a3d;font-weight:bold', ...args);
  const warn = (...args) => console.warn('[cart-rewards]', ...args);
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const currencyRate = () => parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || 1) || 1;
  const activeCurrency = () => (window.Shopify && Shopify.currency && Shopify.currency.active) || config.shopCurrency || 'EUR';
  const locale = () => (window.Shopify && Shopify.locale) || document.documentElement.lang || 'de';

  const formatMoney = (cents, trimZeros = false) => {
    const value = cents / 100;
    const options = { style: 'currency', currency: activeCurrency() };
    if (trimZeros && Number.isInteger(value)) {
      options.minimumFractionDigits = 0;
      options.maximumFractionDigits = 0;
    }
    return new Intl.NumberFormat(locale(), options).format(value);
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const giftKeyOf = (item) => (item && item.properties && item.properties[PROPERTY]) || null;

  /* ------------------------------------------------------------------ */
  /* State computation                                                   */
  /* ------------------------------------------------------------------ */

  const sortedTiers = () => {
    const rate = currencyRate();
    return (config.tiers || [])
      .map((tier) => ({ ...tier, thresholdConverted: Math.round(tier.threshold * rate) }))
      .sort((a, b) => a.thresholdConverted - b.thresholdConverted);
  };

  const qualifyingAmount = (cart) => {
    const items = (cart.items || []).filter((item) => !giftKeyOf(item));

    if (config.basis === 'before_discounts') {
      return items.reduce((sum, item) => sum + item.original_line_price, 0);
    }

    const lines = items.reduce((sum, item) => sum + item.final_line_price, 0);
    const cartDiscount = (cart.cart_level_discount_applications || []).reduce((sum, allocation) => sum + (allocation.total_allocated_amount || 0), 0);

    return Math.max(0, lines - cartDiscount);
  };

  const computeState = (cart) => {
    const amount = qualifyingAmount(cart);
    const tiers = sortedTiers();
    const reached = tiers.filter((tier) => amount >= tier.thresholdConverted);
    const nextIndex = tiers.findIndex((tier) => amount < tier.thresholdConverted);
    const next = nextIndex === -1 ? null : tiers[nextIndex];
    const after = nextIndex === -1 ? null : tiers[nextIndex + 1] || null;
    const configuredMax = config.maxValue ? Math.round(config.maxValue * currencyRate()) : 0;
    const max = configuredMax > 0 ? configuredMax : tiers.length ? tiers[tiers.length - 1].thresholdConverted : 0;

    const isEven = config.spacing === 'even' || config.spacing === 'even_spread';
    const segments = config.spacing === 'even_spread' ? tiers.length + 1 : tiers.length;

    let progress = 0;

    if (tiers.length) {
      if (isEven) {
        progress = 100;
        let previous = 0;
        let allReached = true;

        for (let i = 0; i < tiers.length; i++) {
          const threshold = tiers[i].thresholdConverted;
          if (amount < threshold) {
            const span = Math.max(1, threshold - previous);
            progress = ((i + (amount - previous) / span) / segments) * 100;
            allReached = false;
            break;
          }
          previous = threshold;
        }

        if (allReached && config.spacing === 'even_spread') {
          const span = Math.max(1, max - previous);
          const within = Math.min(1, (amount - previous) / span);
          progress = ((tiers.length + within) / segments) * 100;
        }
      } else if (max > 0) {
        progress = Math.min(100, (amount / max) * 100);
      }
    }

    return { amount, tiers, reached, next, after, max, progress, segments, isEven };
  };

  const buildMessage = (state, context) => {
    const { amount, tiers, reached, next, after } = state;

    if (!tiers.length) {
      return '';
    }

    if (amount === 0 && context === 'product' && config.texts.empty) {
      return config.texts.empty;
    }

    if (!next) {
      return config.texts.allReached || '';
    }

    const remaining = `<strong class="cart-rewards__amount-highlight">${formatMoney(next.thresholdConverted - amount)}</strong>`;
    let template;
    let reward = next.label;
    let nextLabel = after ? after.label : '';

    if (!reached.length) {
      template = next.type === 'shipping' ? config.texts.nextShipping : config.texts.nextGift;
    } else {
      const last = reached[reached.length - 1];
      if (last.type === 'shipping') {
        template = config.texts.shippingReached;
      } else {
        template = config.texts.giftReached;
        reward = last.label;
        nextLabel = next.label;
      }
    }

    return (template || '')
      .replace(/{{\s*amount\s*}}/g, () => remaining)
      .replace(/{{\s*reward\s*}}/g, () => escapeHtml(reward))
      .replace(/{{\s*next\s*}}/g, () => escapeHtml(nextLabel));
  };

  /* ------------------------------------------------------------------ */
  /* Cart fetching                                                       */
  /* ------------------------------------------------------------------ */

  const fetchCart = async () => (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })).json();

  /* ------------------------------------------------------------------ */
  /* <cart-rewards-bar> custom element                                   */
  /* ------------------------------------------------------------------ */

  class CartRewardsBar extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const { signal } = this._abortController;

      document.addEventListener('cart:change', (event) => this.update(event.detail.cart), { signal });
      document.addEventListener('cart:refresh', () => this.refresh(), { signal });
      window.addEventListener('pageshow', (event) => event.persisted && this.refresh(), { signal });

      // Corrects the server-rendered state (e.g. presentment currency) right away.
      this.refresh();
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    async refresh() {
      try {
        this.update(await fetchCart());
      } catch (error) {
        // Keep the server-rendered state.
      }
    }

    update(cart) {
      if (!cart) {
        return;
      }

      const state = computeState(cart);
      const context = this.getAttribute('context') || 'drawer';

      this.setAttribute('total-price', state.amount);
      this.toggleAttribute('all-reached', !state.next && state.tiers.length > 0);

      const track = this.querySelector('[role="progressbar"]');
      if (track) {
        track.setAttribute('aria-valuenow', Math.round(state.progress));
      }

      const message = this.querySelector('[data-cr-message]');
      if (message) {
        message.innerHTML = buildMessage(state, context);
      }

      // Segments: one per tier, fill relative to the previous tier
      let previous = 0;

      state.tiers.forEach((tier) => {
        const segment = this.querySelector(`[data-cr-segment="${tier.key}"]`);
        const span = Math.max(1, tier.thresholdConverted - previous);
        const fill = Math.max(0, Math.min(100, ((state.amount - previous) / span) * 100));
        const isReached = state.amount >= tier.thresholdConverted;

        if (segment) {
          const wasReached = segment.hasAttribute('data-reached');
          segment.toggleAttribute('data-reached', isReached);
          segment.toggleAttribute('data-next', !!state.next && state.next.key === tier.key);

          const fillElement = segment.querySelector('[data-cr-fill]');
          if (fillElement) {
            fillElement.style.width = `${fill}%`;
          }

          if (isReached && !wasReached && this._hasRendered) {
            segment.classList.remove('cart-rewards__segment--pop');
            void segment.offsetWidth; // restart animation
            segment.classList.add('cart-rewards__segment--pop');
          }
        }

        const label = this.querySelector(`[data-cr-label="${tier.key}"]`);
        if (label) {
          label.toggleAttribute('data-reached', isReached);
          const amount = label.querySelector('[data-cr-amount]');
          if (amount) {
            amount.textContent = formatMoney(tier.thresholdConverted, true);
          }
        }

        previous = tier.thresholdConverted;
      });

      // Optional trailing segment up to the max value
      const trailing = this.querySelector('[data-cr-segment="end"]');
      if (trailing) {
        const span = Math.max(1, state.max - previous);
        const fill = Math.max(0, Math.min(100, ((state.amount - previous) / span) * 100));
        const fillElement = trailing.querySelector('[data-cr-fill]');
        if (fillElement) {
          fillElement.style.width = `${fill}%`;
        }
      }

      this._hasRendered = true;
    }
  }

  if (!window.customElements.get('cart-rewards-bar')) {
    window.customElements.define('cart-rewards-bar', CartRewardsBar);
  }

  /* ------------------------------------------------------------------ */
  /* Gift manager – adds/removes gift line items                         */
  /* ------------------------------------------------------------------ */

  class CartRewardsManager {
    constructor() {
      this.busy = false;
      this.pending = null;
      this.failedTiers = new Set();

      document.addEventListener('cart:change', (event) => {
        // Ignore our own broadcast (already evaluated) to avoid needless round-trips.
        if (event.detail && event.detail.cartRewards) {
          return;
        }
        this.evaluate(event.detail.cart, event.detail.baseEvent);
      });
      document.addEventListener('cart:refresh', () => this.evaluateFromServer());
      window.addEventListener('pageshow', (event) => event.persisted && this.evaluateFromServer());

      // Re-check whenever the cart drawer is opened (covers changes made on other tabs/pages).
      document.addEventListener('dialog:after-show', (event) => {
        if (event.target && event.target.matches && event.target.matches('cart-drawer')) {
          this.evaluateFromServer();
        }
      });

      this.interceptCartRequests();
      this.evaluateFromServer();
    }

    /**
     * Fallback for cart mutations that bypass the theme's events (apps, custom scripts, upsell widgets):
     * watches every fetch/XHR to /cart/add|change|update|clear and re-evaluates afterwards.
     * Our own requests are flagged and skipped.
     */
    interceptCartRequests() {
      const pattern = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
      const isCartMutation = (url) => typeof url === 'string' && pattern.test(url);
      const scheduleCheck = () => {
        clearTimeout(this._externalTimer);
        this._externalTimer = setTimeout(() => this.evaluateFromServer(), 150);
      };

      if (window.fetch && !window.fetch.__cartRewardsPatched) {
        const originalFetch = window.fetch;
        const patched = function (input, init) {
          const url = typeof input === 'string' ? input : input && input.url;
          const own = init && init.headers && init.headers['X-Cart-Rewards'];
          const promise = originalFetch.apply(this, arguments);
          if (!own && isCartMutation(url)) {
            promise.then((response) => response.ok && scheduleCheck()).catch(() => {});
          }
          return promise;
        };
        patched.__cartRewardsPatched = true;
        window.fetch = patched;
      }

      if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__cartRewardsPatched) {
        const originalOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function (method, url) {
          if (isCartMutation(url)) {
            this.addEventListener('load', () => this.status >= 200 && this.status < 300 && scheduleCheck());
          }
          return originalOpen.apply(this, arguments);
        };
        XMLHttpRequest.prototype.__cartRewardsPatched = true;
      }

    }

    evaluateFromServer(baseEvent = null) {
      // The cart is fetched inside evaluate(), after the lock is taken – a cart fetched before that can be stale.
      return this.evaluate(null, baseEvent);
    }

    /**
     * Safety net against runaway loops (e.g. a gift being re-added over and over, or endless reloads on the
     * cart page): at most MAX_RUNS mutating runs per RUN_WINDOW, counted across page loads via sessionStorage.
     */
    allowRun() {
      const MAX_RUNS = 6;
      const RUN_WINDOW = 60000;
      const now = Date.now();
      let runs = [];

      try {
        runs = JSON.parse(sessionStorage.getItem('cartRewardsRuns') || '[]').filter((at) => now - at < RUN_WINDOW);
      } catch (error) {
        runs = (this._runs || []).filter((at) => now - at < RUN_WINDOW);
      }

      if (runs.length >= MAX_RUNS) {
        warn(`more than ${MAX_RUNS} gift updates within ${RUN_WINDOW / 1000}s – pausing automatic gift handling`);
        return false;
      }

      runs.push(now);
      this._runs = runs;
      try {
        sessionStorage.setItem('cartRewardsRuns', JSON.stringify(runs));
      } catch (error) {
        // In-memory fallback only.
      }

      return true;
    }

    /** Debug helper: window.__cartRewardsManager.inspect() */
    async inspect() {
      const cart = await fetchCart();
      const state = computeState(cart);
      const diff = this.diff(cart);
      const result = {
        config,
        amount: state.amount,
        amountFormatted: formatMoney(state.amount),
        tiers: state.tiers.map((t) => ({ key: t.key, threshold: t.thresholdConverted, reached: state.amount >= t.thresholdConverted, variantId: t.variantId, available: t.available })),
        giftLinesInCart: cart.items.filter(giftKeyOf).map((i) => ({ key: giftKeyOf(i), line: i.key, variant: i.variant_id, quantity: i.quantity, price: i.final_line_price })),
        wouldAdd: diff.adds.map((t) => t.key),
        wouldRemove: diff.removes.map((i) => giftKeyOf(i)),
        failedTiers: [...this.failedTiers],
        busy: this.busy,
        lastRun: this.lastRun || null
      };
      console.table(result.tiers);
      console.log('[cart-rewards] inspect', result);
      return result;
    }

    diff(cart) {
      const state = computeState(cart);
      const wanted = new Map();

      state.reached
        .filter((tier) => tier.type === 'gift' && tier.variantId && !this.failedTiers.has(tier.key))
        .forEach((tier) => wanted.set(tier.key, tier));

      const removes = [];
      const quantityFixes = [];
      const present = new Set();

      (cart.items || []).forEach((item) => {
        const key = giftKeyOf(item);
        if (!key) {
          return;
        }

        const tier = wanted.get(key);
        const configuredTier = state.tiers.find((t) => t.key === key);
        const belowThreshold = configuredTier && state.amount < configuredTier.thresholdConverted;
        const variantMismatch = configuredTier && configuredTier.variantId !== item.variant_id;
        const duplicate = present.has(key);

        if (!configuredTier || variantMismatch || duplicate || (belowThreshold && config.autoRemove)) {
          removes.push(item);
          return;
        }

        present.add(key);

        if (item.quantity > 1) {
          quantityFixes.push(item);
        }

        if (tier) {
          wanted.delete(key);
        }
      });

      const adds = [...wanted.values()].filter((tier) => !present.has(tier.key));

      return { adds, removes, quantityFixes };
    }

    /**
     * A cart passed in (from a theme event) is only used for a cheap "nothing to do" check. Every run that may
     * mutate the cart takes the lock first and then fetches a fresh cart, so concurrent triggers (theme event,
     * fetch interceptor, drawer open, …) can never act on a cart that predates another run's gift add.
     */
    async evaluate(cart, baseEvent = null) {
      if (this.busy) {
        // Another run is in progress; re-check from the server once it is done.
        this.pending = { baseEvent };
        log('busy, queued re-check');
        return;
      }

      if (cart) {
        const quick = this.diff(cart);
        if (!quick.adds.length && !quick.removes.length && !quick.quantityFixes.length) {
          return;
        }
      }

      this.busy = true;

      try {
        cart = await fetchCart();

        const state = computeState(cart);
        const { adds, removes, quantityFixes } = this.diff(cart);

        log('evaluate', {
          baseEvent,
          amount: state.amount,
          reached: state.reached.map((t) => t.key),
          next: state.next && state.next.key,
          adds: adds.map((t) => `${t.key} (variant ${t.variantId})`),
          removes: removes.map((i) => `${giftKeyOf(i)} line ${i.key}`),
          quantityFixes: quantityFixes.map((i) => i.key),
          failedTiers: [...this.failedTiers]
        });

        if (!adds.length && !removes.length && !quantityFixes.length) {
          return;
        }

        if (!this.allowRun()) {
          return;
        }

        this.lastRun = { at: Date.now(), adds: adds.map((t) => t.key), removes: removes.map((i) => giftKeyOf(i)), errors: [] };

        let changed = false;

        for (const item of removes) {
          changed = (await this.changeLine(item.key, 0)) || changed;
        }

        for (const item of quantityFixes) {
          changed = (await this.changeLine(item.key, 1)) || changed;
        }

        for (const tier of adds) {
          const ok = await this.addGift(tier);
          if (!ok) {
            this.failedTiers.add(tier.key);
            // Retry later (e.g. temporary inventory / network issue)
            setTimeout(() => {
              this.failedTiers.delete(tier.key);
              this.evaluateFromServer();
            }, 30000);
          }
          changed = ok || changed;
        }

        if (changed) {
          await this.broadcast(baseEvent);
        }
      } catch (error) {
        warn('evaluation failed', error);
        this.lastRun && this.lastRun.errors.push(String(error));
      } finally {
        this.busy = false;

        if (this.pending) {
          const pending = this.pending;
          this.pending = null;
          this.evaluateFromServer(pending.baseEvent);
        }
      }
    }

    async changeLine(lineKey, quantity) {
      const response = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Cart-Rewards': '1' },
        body: JSON.stringify({ id: lineKey, quantity })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        warn(`change.js failed for line ${lineKey}`, response.status, body);
        this.lastRun && this.lastRun.errors.push(`change ${lineKey}: ${response.status} ${body.description || body.message || ''}`);
      } else {
        log(`line ${lineKey} set to quantity ${quantity}`);
      }

      return response.ok;
    }

    async addGift(tier) {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Cart-Rewards': '1' },
        body: JSON.stringify({
          items: [{ id: tier.variantId, quantity: 1, properties: { [PROPERTY]: tier.key } }]
        })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        warn(`add.js failed for gift ${tier.key} (variant ${tier.variantId})`, response.status, body);
        this.lastRun && this.lastRun.errors.push(`add ${tier.key}: ${response.status} ${body.description || body.message || ''}`);
        document.dispatchEvent(new CustomEvent('cart-rewards:error', { detail: { tier, status: response.status, body } }));
      } else {
        log(`gift ${tier.key} added (variant ${tier.variantId})`);
      }

      return response.ok;
    }

    /**
     * Fetches the fresh cart plus the bundled sections (cart drawer, …) and re-dispatches the theme's
     * "cart:change" event so every component (drawer, count bubble, this bar) re-renders in place.
     *
     * The drawer never opens because of this: opening is only triggered by the theme's "variant:add"
     * event, which we never dispatch. The drawer's "cart:change" handler swaps the inner HTML either
     * immediately (baseEvent "variant:add") or after a 1.25s delay (any other base event, used for the
     * line-item removal animation). We mirror the triggering base event so our re-render is always
     * applied AFTER the theme's own (possibly delayed) re-render and never overwritten by stale HTML.
     */
    async broadcast(baseEvent = null) {
      const pageType = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType;

      if (pageType === 'cart') {
        window.location.reload();
        return;
      }

      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

      const cart = await fetchCart();
      cart.sections = {};
      log('broadcast', { sections, baseEvent });

      if (sections.length) {
        try {
          const uniqueSections = [...new Set(sections)];
          cart.sections = await (await fetch(`${root()}?sections=${uniqueSections.join(',')}`)).json();
        } catch (error) {
          cart.sections = {};
        }
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: baseEvent === 'variant:add' ? 'variant:add' : 'cart-rewards:change',
          cart,
          cartRewards: true
        }
      }));
    }
  }

  const start = () => {
    if (!window.__cartRewardsManager) {
      window.__cartRewardsManager = new CartRewardsManager();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
